import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  CheckBox,
  color,
  spacing,
  toast,
  Typography
} from '@uniquegood/realworld-studio-design';
import { TableStyle, textShortening } from '@/styles';
import { coreApi } from '@/api';
import useRequest from '@/hooks/useRequest';
import { genres } from '@/pages/detail/const';

interface ChangeTesterContentModalContentProps {
  /** 이미 등록된 테스터의 userId (기존 테스터 콘텐츠 변경 시 사용) */
  userId?: string;
  /** 아직 등록되지 않은 테스터 정보 (새 테스터 추가 시 사용) */
  pendingTester?: AccountResponseModel;
  /** 테스터로 역할 변경할 기존 멤버 정보 (소유자/편집자 → 테스터 변경 시 사용) */
  memberToConvert?: { id: string; name: string };
  onSaveSuccess?: () => void;
}

async function addMember(appId: string, memberId: string) {
  return coreApi.post(
    `/apps/${appId}/members`,
    { userId: memberId, level: 'Tester' },
    { headers: { 'x-rwd-api-version': '1.2' } }
  );
}

async function updateMemberToTester(appId: string, memberId: string) {
  return coreApi.put(
    `/apps/${appId}/members/${memberId}`,
    { level: 'Tester' },
    { headers: { 'x-rwd-api-version': '1.2' } }
  );
}

const PROJECT_TYPE_LABEL: Record<ProjectGameType, string> = {
  Online: '온라인',
  Offline: '오프라인',
  OnlineKit: '온라인',
  OfflineKit: '오프라인'
};

function ChangeTesterContentModalContent({
  userId,
  pendingTester,
  memberToConvert,
  onSaveSuccess
}: ChangeTesterContentModalContentProps) {
  const { appId } = useParams<AppParam>();
  const isNewTesterMode = !!pendingTester;
  const isConvertMode = !!memberToConvert;
  const targetUserId = userId ?? pendingTester?.id ?? memberToConvert?.id;

  // 기존 테스터: 할당된 프로젝트 목록 조회
  const { data: assignedProjectsData } = useRequest<
    CommonResponseModel<AssignedProjectResponseModel[]>
  >(
    appId && userId && !isNewTesterMode && !isConvertMode
      ? `/apps/${appId}/members/testers/${userId}/assignedProjects`
      : null
  );

  // 새 테스터 또는 역할 변경: 프로젝트 목록 조회
  const { data: allProjectsData } = useRequest<CommonResponseModel<AssignedProjectResponseModel[]>>(
    appId && (isNewTesterMode || isConvertMode) && targetUserId
      ? `/apps/${appId}/members/testers/${targetUserId}/assignedProjects`
      : null
  );

  const [contents, setContents] = useState<AssignedProjectResponseModel[]>([]);
  const [initialAssignedIds, setInitialAssignedIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const genresMap = useMemo(() => {
    return new Map(genres.map((genre) => [genre.key, genre.name]));
  }, [genres]);

  useEffect(() => {
    const projectsData = isNewTesterMode || isConvertMode ? allProjectsData : assignedProjectsData;
    if (projectsData?.data) {
      const initialData = projectsData.data;
      setContents(initialData);
      setInitialAssignedIds(
        new Set(initialData.filter((project) => project.isAssigned).map((project) => project.id))
      );
    }
  }, [isNewTesterMode, isConvertMode, assignedProjectsData, allProjectsData]);

  const hasChanges = useMemo(() => {
    const currentAssignedIds = new Set(
      contents.filter((content) => content.isAssigned).map((content) => content.id)
    );

    if (currentAssignedIds.size === 0) {
      return false;
    }

    if (currentAssignedIds.size !== initialAssignedIds.size) {
      return true;
    }

    for (const id of currentAssignedIds) {
      if (!initialAssignedIds.has(id)) {
        return true;
      }
    }

    return false;
  }, [contents, initialAssignedIds]);

  const isAllSelected = useMemo(() => {
    return contents.length > 0 && contents.every((content) => content.isAssigned);
  }, [contents]);

  const toggleSelectAll = useCallback(() => {
    setContents((prev) => prev.map((content) => ({ ...content, isAssigned: !isAllSelected })));
  }, [isAllSelected]);

  const toggleSelect = (id: string) => {
    setContents((prev) =>
      prev.map((content) =>
        content.id === id ? { ...content, isAssigned: !content.isAssigned } : content
      )
    );
  };

  const handleSave = useCallback(async () => {
    if (!appId || !targetUserId) return;

    const selectedProjectIds = contents
      .filter((content) => content.isAssigned)
      .map((content) => content.id);

    setIsSaving(true);
    try {
      // 새 테스터 모드: 먼저 테스터 등록 API 호출
      if (isNewTesterMode && pendingTester) {
        await addMember(appId, pendingTester.id);
        toast({ message: `${pendingTester.name}님을 테스터로 추가했어요!` });
      }

      // 역할 변경 모드: 먼저 테스터로 역할 변경 API 호출
      if (isConvertMode && memberToConvert) {
        await updateMemberToTester(appId, memberToConvert.id);
        toast({ message: `${memberToConvert.name}님을 테스터로 변경했어요!` });
      }

      // 콘텐츠 할당 API 호출
      await coreApi.post(`/apps/${appId}/members/testers/${targetUserId}/assignProject`, {
        projectIds: selectedProjectIds
      });
      toast({ message: '테스트 콘텐츠가 저장되었습니다.' });
      onSaveSuccess?.();
    } finally {
      setIsSaving(false);
    }
  }, [
    appId,
    targetUserId,
    isNewTesterMode,
    isConvertMode,
    pendingTester,
    memberToConvert,
    contents,
    onSaveSuccess
  ]);

  return (
    <div css={WrapperStyle}>
      <div css={ContentAreaStyle}>
        <div css={SelectAllWrapperStyle}>
          <CheckBox
            checked={isAllSelected}
            onChange={toggleSelectAll}
            disabled={contents.length === 0}
          >
            전체 선택
          </CheckBox>
        </div>
        <div css={ContainerStyle}>
          <table css={[TableStyle, CustomTableStyle]}>
            <thead>
              <tr>
                <th css={ThumbnailColumnStyle}>대표 이미지</th>
                <th css={ContentNameColumnStyle}>콘텐츠명</th>
                <th css={DescriptionColumnStyle}>한 줄 설명</th>
                <th css={TypeColumnStyle}>타입</th>
                <th css={GenreColumnStyle}>장르</th>
                <th css={SelectColumnStyle}>선택</th>
              </tr>
            </thead>
            <tbody>
              {contents.length === 0 ? (
                <tr>
                  <td colSpan={6} css={EmptyRowStyle}>
                    <Typography as="p" type="body" textColor="disabled">
                      만든 콘텐츠가 아직 없어요.
                    </Typography>
                  </td>
                </tr>
              ) : (
                contents.map((content) => (
                  <tr key={content.id}>
                    <td css={ThumbnailColumnStyle}>
                      {content.imageUrl ? (
                        <img src={content.imageUrl} alt={content.name} css={ThumbnailImageStyle} />
                      ) : (
                        <div css={ThumbnailPlaceholderStyle} />
                      )}
                    </td>
                    <td css={ContentNameColumnStyle}>
                      <Typography as="p" type="body" cssStyle={textShortening}>
                        {content.name}
                      </Typography>
                    </td>
                    <td css={DescriptionColumnStyle}>
                      <Typography as="p" type="body" cssStyle={textShortening}>
                        {content.description ?? '-'}
                      </Typography>
                    </td>
                    <td css={TypeColumnStyle}>
                      <Typography as="p" type="body">
                        {PROJECT_TYPE_LABEL[content.type]}
                      </Typography>
                    </td>
                    <td css={GenreColumnStyle}>
                      <Typography as="p" type="body" cssStyle={textShortening}>
                        {content.genres?.map((genre) => genresMap.get(genre as Genre)).join(', ') ??
                          '-'}
                      </Typography>
                    </td>
                    <td css={SelectColumnStyle}>
                      <div css={CheckboxWrapperStyle}>
                        <CheckBox
                          checked={content.isAssigned}
                          onChange={() => toggleSelect(content.id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div css={FooterStyle}>
        <Button
          type="primary"
          size="large"
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || !hasChanges}
        >
          저장
        </Button>
      </div>
    </div>
  );
}

const WrapperStyle = css`
  display: flex;
  flex-direction: column;
  height: 720px;
  overflow: hidden;
  margin-left: -${spacing.padding.xlarge2};
  margin-right: -${spacing.padding.xlarge2};
`;

const ContentAreaStyle = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-left: ${spacing.padding.xlarge2};
  padding-right: ${spacing.padding.xlarge2};
`;

const ContainerStyle = css`
  margin-bottom: ${spacing.margin.large};
`;

const CustomTableStyle = css`
  width: 100%;
  border: 1px solid ${color.border_default_subdued};
  border-radius: 8px;
  overflow: hidden;
  border-collapse: separate;
  border-spacing: 0;

  td {
    height: 52px;
    padding: 6px 16px;
    vertical-align: middle;
  }

  /* 마지막 행의 하단 border 제거 */
  tbody tr:last-child td {
    box-shadow: none;
  }
`;

const ThumbnailColumnStyle = css`
  width: 96px;
  min-width: 96px;
  text-align: center;
`;

const TypeColumnStyle = css`
  width: 120px;
  min-width: 120px;
  max-width: 120px;
`;

const ContentNameColumnStyle = css`
  width: 236px;
  min-width: 236px;
  max-width: 236px;
`;

const DescriptionColumnStyle = css`
  width: 236px;
  min-width: 236px;
  max-width: 236px;
`;

const GenreColumnStyle = css`
  width: 160px;
  min-width: 160px;
  max-width: 160px;
`;

const SelectColumnStyle = css`
  width: 64px;
  min-width: 64px;
  text-align: center;
`;

const ThumbnailImageStyle = css`
  display: block;
  width: 40px;
  height: 30px;
  border-radius: 4px;
  object-fit: cover;
  margin: 0 auto 0 0;
`;

const ThumbnailPlaceholderStyle = css`
  width: 40px;
  height: 30px;
  background-color: #d9d9d9;
  border-radius: 4px;
  margin: 0 auto 0 0;
`;

const EmptyRowStyle = css`
  padding: 16px;
`;

const SelectAllWrapperStyle = css`
  display: flex;
  justify-content: flex-end;
  padding: 4px 0;
  margin-bottom: ${spacing.margin.small};
`;

const CheckboxWrapperStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const FooterStyle = css`
  display: flex;
  justify-content: flex-end;
  padding-top: ${spacing.padding.large};
  padding-left: ${spacing.padding.xlarge2};
  padding-right: ${spacing.padding.xlarge2};
  border-top: 1px solid ${color.border_default_subdued};
`;

export default memo(ChangeTesterContentModalContent);
